const BUTTON = Object.freeze({
  confirm: 0, cancel: 1, lock: 2, reroll: 3,
  pause: 9, up: 12, down: 13, left: 14, right: 15,
});

const ACTIONS = Object.freeze(Object.keys(BUTTON));

const LABELS = Object.freeze({
  xbox: Object.freeze({ confirm: 'A', cancel: 'B', lock: 'X', reroll: 'Y', pause: 'MENU' }),
  playstation: Object.freeze({ confirm: '×', cancel: '○', lock: '□', reroll: '△', pause: 'OPTIONS' }),
  generic: Object.freeze({ confirm: '1', cancel: '2', lock: '3', reroll: '4', pause: 'START' }),
});

export function gamepadFamily(id = '') {
  const name = String(id);
  // Chrome은 최신 Xbox 패드를 "Xbox Wireless Controller"로 노출한다.
  // 아래 PlayStation의 범용 "Wireless Controller" 표식보다 Xbox를 먼저
  // 판별하지 않으면 이 장치가 PlayStation으로 잘못 분류되어 진동 배선이 끊긴다.
  if (/xbox|xinput|microsoft|vendor:\s*045e/i.test(name)) return 'xbox';
  if (/dualsense|dualshock|playstation|wireless controller|sony|vendor:\s*054c/i.test(name)) {
    return 'playstation';
  }
  return 'generic';
}

export function gamepadLabels(family = 'generic') {
  return LABELS[family] ?? LABELS[gamepadFamily(family)];
}

export function playerHpDrop(previousHp, currentHp) {
  const before = Math.max(0, Number(previousHp) || 0);
  const after = Math.max(0, Number(currentHp) || 0);
  return Math.max(0, before - after);
}

function defaultPads() {
  return globalThis.navigator?.getGamepads?.() ?? [];
}

function firstConnected(getGamepads) {
  const pads = getGamepads?.() ?? [];
  for (const pad of pads) if (pad?.connected !== false) return pad;
  return null;
}

function connectedPadByIndex(getGamepads, index) {
  if (!Number.isInteger(index) || index < 0) return null;
  const pads = getGamepads?.() ?? [];
  for (const pad of pads) {
    if (pad?.connected !== false && Number(pad?.index) === index) return pad;
  }
  return null;
}

function buttonDown(pad, index) {
  const button = pad?.buttons?.[index];
  return !!(button?.pressed || Number(button?.value) > 0.5);
}

function deadAxis(value, deadzone) {
  const raw = Math.max(-1, Math.min(1, Number(value) || 0));
  const magnitude = Math.abs(raw);
  if (magnitude <= deadzone) return 0;
  return Math.sign(raw) * Math.min(1, (magnitude - deadzone) / (1 - deadzone));
}

// Aim keeps the stick's real angle, so its deadzone must be radial rather than
// clipping X/Y independently. That matters most near the four cardinal axes.
function deadStick(x, y, deadzone) {
  const rawX = Math.max(-1, Math.min(1, Number(x) || 0));
  const rawY = Math.max(-1, Math.min(1, Number(y) || 0));
  const magnitude = Math.hypot(rawX, rawY);
  if (magnitude <= deadzone) return { x: 0, y: 0, active: false };
  const scaled = Math.min(1, (magnitude - deadzone) / (1 - deadzone));
  return {
    x: rawX / magnitude * scaled,
    y: rawY / magnitude * scaled,
    active: true,
  };
}

function hasActiveInput(pad, threshold = 0.35) {
  if (!pad || pad.connected === false) return false;
  if (Array.from(pad.buttons ?? []).some((button) => button?.pressed || Number(button?.value) > 0.5)) {
    return true;
  }
  return Array.from(pad.axes ?? []).slice(0, 4)
    .some((axis) => Math.abs(Number(axis) || 0) >= threshold);
}

function hasHapticActuator(pad) {
  return !!(pad?.vibrationActuator ?? pad?.hapticActuators?.[0]);
}

const SONY_VENDOR_ID = 0x054c;
const DUALSENSE_PRODUCT_IDS = new Set([0x0ce6, 0x0df2]);
const DUALSENSE_USB_REPORT_ID = 0x02;
// Report 0x02 declares 0x2f (47) output bytes in the DualSense USB HID
// descriptor. WebHID supplies the report id separately and validates the
// payload against that descriptor; the Linux driver's 63-byte endpoint buffer
// must not be copied verbatim into sendReport().
const DUALSENSE_USB_PAYLOAD_SIZE = 47;
const DUALSENSE_COMPATIBLE_RUMBLE_FLAGS = 0x03;

function clamp01(value) {
  return Math.max(0, Math.min(1, Number(value) || 0));
}

function isDualSenseDevice(device) {
  return Number(device?.vendorId) === SONY_VENDOR_ID
    && DUALSENSE_PRODUCT_IDS.has(Number(device?.productId));
}

function hasDualSenseUsbOutput(device) {
  const reports = Array.from(device?.collections ?? [])
    .flatMap((collection) => Array.from(collection?.outputReports ?? []));
  return reports.length === 0
    || reports.some((report) => Number(report?.reportId) === DUALSENSE_USB_REPORT_ID);
}

/**
 * Direct DualSense/Edge USB compatible-rumble output for Chrome WebHID.
 * The report id is supplied separately to sendReport, so the payload is the
 * descriptor-declared 47 bytes: flags, weak motor, strong motor, untouched data.
 */
export class DualSenseUsbHaptics {
  constructor({
    hid = globalThis.navigator?.hid,
    setTimer = globalThis.setTimeout?.bind(globalThis),
    clearTimer = globalThis.clearTimeout?.bind(globalThis),
    onStatus = null,
  } = {}) {
    this.hid = hid;
    this.setTimer = setTimer;
    this.clearTimer = clearTimer;
    this.onStatus = onStatus;
    this.device = null;
    this.status = this.supported ? 'idle' : 'unsupported';
    this.stopTimer = null;
    this.sendQueue = Promise.resolve();
    this._onConnect = (event) => {
      if (!this.device && isDualSenseDevice(event?.device)) this.attach(event.device);
    };
    this._onDisconnect = (event) => {
      if (event?.device !== this.device) return;
      if (this.stopTimer !== null) this.clearTimer?.(this.stopTimer);
      this.stopTimer = null;
      this.device = null;
      this.setStatus('idle');
    };
    this.hid?.addEventListener?.('connect', this._onConnect);
    this.hid?.addEventListener?.('disconnect', this._onDisconnect);
  }

  get supported() { return typeof this.hid?.requestDevice === 'function'; }
  get connected() { return !!(this.device?.opened && isDualSenseDevice(this.device)); }

  setStatus(status, error = null) {
    this.status = status;
    try { this.onStatus?.({ status, error, device: this.device }); }
    catch { /* A status label must never interrupt controller output. */ }
  }

  async attach(device) {
    if (!isDualSenseDevice(device) || !hasDualSenseUsbOutput(device)) {
      this.setStatus('error', new Error('DualSense USB output report is unavailable'));
      return false;
    }
    try {
      if (!device.opened) await device.open();
      this.device = device;
      this.setStatus('connected');
      return true;
    } catch (error) {
      this.device = null;
      this.setStatus('error', error);
      return false;
    }
  }

  async restore() {
    if (!this.supported || typeof this.hid?.getDevices !== 'function') return false;
    try {
      const devices = await this.hid.getDevices();
      const device = Array.from(devices ?? []).find((item) =>
        isDualSenseDevice(item) && hasDualSenseUsbOutput(item));
      return device ? this.attach(device) : false;
    } catch (error) {
      this.setStatus('error', error);
      return false;
    }
  }

  async request() {
    if (!this.supported) return false;
    this.setStatus('requesting');
    try {
      // This call deliberately happens before the first await. Chrome requires
      // requestDevice to remain inside the user's click activation.
      const choice = this.hid.requestDevice({
        filters: Array.from(DUALSENSE_PRODUCT_IDS, (productId) => ({
          vendorId: SONY_VENDOR_ID, productId,
        })),
      });
      const devices = await choice;
      const device = Array.from(devices ?? []).find((item) =>
        isDualSenseDevice(item) && hasDualSenseUsbOutput(item));
      if (!device) {
        this.setStatus('idle');
        return false;
      }
      return this.attach(device);
    } catch (error) {
      // Cancelling Chrome's chooser is an idle state, not a broken controller.
      this.setStatus(error?.name === 'NotFoundError' ? 'idle' : 'error', error);
      return false;
    }
  }

  report(strongMagnitude = 0, weakMagnitude = 0) {
    const payload = new Uint8Array(DUALSENSE_USB_PAYLOAD_SIZE);
    payload[0] = DUALSENSE_COMPATIBLE_RUMBLE_FLAGS;
    payload[2] = Math.round(clamp01(weakMagnitude) * 255);
    payload[3] = Math.round(clamp01(strongMagnitude) * 255);
    return payload;
  }

  send(strongMagnitude = 0, weakMagnitude = 0) {
    const device = this.device;
    if (!device?.opened || typeof device.sendReport !== 'function') return false;
    const payload = this.report(strongMagnitude, weakMagnitude);
    this.sendQueue = this.sendQueue.catch(() => false).then(async () => {
      if (!device.opened) return false;
      try {
        await device.sendReport(DUALSENSE_USB_REPORT_ID, payload);
        return true;
      } catch (error) {
        if (this.device === device) this.setStatus('error', error);
        return false;
      }
    });
    return true;
  }

  async test() {
    if (!this.connected) return false;
    this.setStatus('testing');
    if (!this.play({ duration: 180, strongMagnitude: 0.42, weakMagnitude: 0.28 })) {
      this.setStatus('error', new Error('DualSense vibration output could not start'));
      return false;
    }
    const sent = await this.sendQueue;
    if (sent) this.setStatus('connected');
    return sent;
  }

  play({ duration = 0, strongMagnitude = 0, weakMagnitude = 0 } = {}) {
    if (!this.connected) return false;
    if (this.stopTimer !== null) this.clearTimer?.(this.stopTimer);
    this.stopTimer = null;
    if (!this.send(strongMagnitude, weakMagnitude)) return false;
    const ms = Math.max(0, Math.round(Number(duration) || 0));
    if (ms > 0) {
      this.stopTimer = this.setTimer?.(() => {
        this.stopTimer = null;
        this.send(0, 0);
      }, ms) ?? null;
    }
    return true;
  }

  stop() {
    if (this.stopTimer !== null) this.clearTimer?.(this.stopTimer);
    this.stopTimer = null;
    return this.send(0, 0);
  }

  destroy() {
    this.stop();
    this.hid?.removeEventListener?.('connect', this._onConnect);
    this.hid?.removeEventListener?.('disconnect', this._onDisconnect);
    this.device = null;
  }
}

/** Xbox, DualSense/DualShock and generic standard-layout input, read once per frame. */
export class StandardGamepadInput {
  constructor({ getGamepads = defaultPads, deadzone = 0.22, directionThreshold = 0.58 } = {}) {
    this.getGamepads = getGamepads;
    this.deadzone = Math.max(0, Math.min(0.8, deadzone));
    this.directionThreshold = Math.max(this.deadzone, Math.min(0.95, directionThreshold));
    this.previous = Object.fromEntries(ACTIONS.map((action) => [action, false]));
    this.activeIndex = -1;
  }

  reset() {
    for (const action of ACTIONS) this.previous[action] = false;
  }

  read() {
    const pads = Array.from(this.getGamepads?.() ?? []).filter((pad) => pad?.connected !== false);
    const current = pads.find((pad) => Number(pad?.index) === this.activeIndex) ?? null;
    const activePads = pads.filter((pad) => hasActiveInput(pad));
    const active = current && hasActiveInput(current) ? current
      : activePads.find((pad) => gamepadFamily(pad?.id) === 'playstation' && hasHapticActuator(pad))
        ?? activePads.find((pad) => hasHapticActuator(pad))
        ?? activePads[0]
        ?? null;
    const pad = active ?? current ?? pads[0] ?? null;
    if (!pad) {
      this.reset();
      this.activeIndex = -1;
      return {
        connected: false, id: '', family: 'generic', index: -1,
        moveX: 0, moveY: 0, aimX: 0, aimY: 0, aimActive: false,
        held: { ...this.previous }, pressed: { ...this.previous },
      };
    }

    const nextIndex = Number(pad.index ?? 0);
    if (nextIndex !== this.activeIndex) this.reset();
    this.activeIndex = nextIndex;

    let moveX = deadAxis(pad.axes?.[0], this.deadzone);
    let moveY = deadAxis(pad.axes?.[1], this.deadzone);
    const aim = deadStick(pad.axes?.[2], pad.axes?.[3], this.deadzone);
    const dpad = {
      up: buttonDown(pad, BUTTON.up), down: buttonDown(pad, BUTTON.down),
      left: buttonDown(pad, BUTTON.left), right: buttonDown(pad, BUTTON.right),
    };
    if (dpad.left || dpad.right) moveX = (dpad.right ? 1 : 0) - (dpad.left ? 1 : 0);
    if (dpad.up || dpad.down) moveY = (dpad.down ? 1 : 0) - (dpad.up ? 1 : 0);

    const held = {};
    for (const action of ACTIONS) held[action] = buttonDown(pad, BUTTON[action]);
    held.left ||= moveX <= -this.directionThreshold;
    held.right ||= moveX >= this.directionThreshold;
    held.up ||= moveY <= -this.directionThreshold;
    held.down ||= moveY >= this.directionThreshold;

    const pressed = {};
    for (const action of ACTIONS) pressed[action] = held[action] && !this.previous[action];
    this.previous = { ...held };
    const id = String(pad.id ?? '');
    return {
      connected: true, id, family: gamepadFamily(id), index: Number(pad.index ?? 0),
      moveX, moveY, aimX: aim.x, aimY: aim.y, aimActive: aim.active, held, pressed,
    };
  }
}
function supportedEffects(actuator) {
  const effects = actuator?.effects;
  if (!effects || typeof effects[Symbol.iterator] !== 'function') return null;
  return new Set(Array.from(effects, String));
}

/**
 * Dual-rumble/pulse feedback for Xbox and PlayStation pads in Chromium.
 * Small contacts form short taps; an area contact cancels them and takes priority.
 */
export class GamepadHitHaptics {
  constructor({
    getGamepads = defaultPads,
    setTimer = globalThis.setTimeout?.bind(globalThis),
    clearTimer = globalThis.clearTimeout?.bind(globalThis),
    now = () => globalThis.performance?.now?.() ?? Date.now(),
    externalHaptics = null,
  } = {}) {
    this.getGamepads = getGamepads;
    this.setTimer = setTimer;
    this.clearTimer = clearTimer;
    this.now = now;
    this.timers = [];
    this.nextTapAt = 0;
    this.blockedUntil = 0;
    this.activeIndex = -1;
    this.externalHaptics = externalHaptics;
  }

  setActivePad(padOrIndex) {
    const index = typeof padOrIndex === 'object' ? padOrIndex?.index : padOrIndex;
    this.activeIndex = Number.isInteger(Number(index)) && Number(index) >= 0 ? Number(index) : -1;
    return this;
  }

  pad() {
    return connectedPadByIndex(this.getGamepads, this.activeIndex)
      ?? firstConnected(this.getGamepads);
  }

  actuators() {
    const pad = this.pad();
    const candidates = [pad?.vibrationActuator, ...(pad?.hapticActuators ?? [])];
    return candidates.filter((actuator, index) => actuator && candidates.indexOf(actuator) === index);
  }

  actuator() {
    return this.actuators()[0] ?? null;
  }

  tryCandidates(candidates, index = 0) {
    for (let i = index; i < candidates.length; i++) {
      try {
        const result = candidates[i]();
        if (result?.catch) result.catch(() => this.tryCandidates(candidates, i + 1));
        return true;
      } catch { /* Try the next actuator/effect without interrupting combat. */ }
    }
    return false;
  }

  play({ duration, strongMagnitude, weakMagnitude }) {
    const pad = this.pad();
    if (gamepadFamily(pad?.id) === 'playstation'
        && this.externalHaptics?.connected
        && this.externalHaptics.play({ duration, strongMagnitude, weakMagnitude })) {
      return true;
    }
    const actuators = this.actuators();
    if (!actuators.length) return false;
    const candidates = [];
    for (const actuator of actuators) {
      if (typeof actuator.playEffect !== 'function') continue;
      const effects = supportedEffects(actuator);
      const effect = !effects || effects.has('dual-rumble')
        ? 'dual-rumble'
        : effects.has('trigger-rumble') ? 'trigger-rumble' : null;
      if (effect) {
        candidates.push(() => actuator.playEffect(effect, {
          startDelay: 0, duration, strongMagnitude, weakMagnitude,
          ...(effect === 'trigger-rumble' ? { leftTrigger: 0, rightTrigger: 0 } : {}),
        }));
      }
    }
    for (const actuator of actuators) {
      if (typeof actuator.pulse === 'function') {
        candidates.push(() => actuator.pulse(Math.max(strongMagnitude, weakMagnitude), duration));
      }
    }
    return this.tryCandidates(candidates);
  }

  clearPattern() {
    for (const timer of this.timers) this.clearTimer?.(timer);
    this.timers.length = 0;
  }

  /** One to four crisp taps; repeated hit frames naturally continue the rhythm. */
  hit(count = 1) {
    const at = this.now();
    if (at < this.blockedUntil || at < this.nextTapAt) return;
    this.clearPattern();
    const taps = Math.max(1, Math.min(4, Math.round(count) || 1));
    const tap = (i) => {
      if (this.now() < this.blockedUntil) return;
      this.play({
        duration: 28,
        strongMagnitude: 0.08 + Math.min(0.08, (taps - 1) * 0.02),
        weakMagnitude: 0.26 + Math.min(0.12, i * 0.04),
      });
    };
    tap(0);
    for (let i = 1; i < taps; i++) {
      const timer = this.setTimer?.(() => tap(i), i * 52);
      if (timer !== undefined) this.timers.push(timer);
    }
    this.nextTapAt = at + taps * 52;
  }

  /** A deeper, longer pulse for orbit and splash/area contacts. */
  area(count = 1) {
    const at = this.now();
    if (at < this.blockedUntil) return;
    this.clearPattern();
    const weight = Math.min(1, Math.max(0, (Math.max(1, count) - 1) / 5));
    const duration = Math.round(105 + weight * 75);
    this.play({
      duration,
      strongMagnitude: 0.52 + weight * 0.30,
      weakMagnitude: 0.34 + weight * 0.20,
    });
    this.blockedUntil = at + duration;
    this.nextTapAt = this.blockedUntil;
  }

  /** Every real player HP loss lands as the same unmistakably strong pulse. */
  hurt(damage = 0) {
    const amount = Math.max(0, Number(damage) || 0);
    if (amount <= 0) return false;
    const at = this.now();
    if (at < this.blockedUntil) return false;
    this.clearPattern();
    const duration = 150;
    const played = this.play({
      duration,
      strongMagnitude: 1,
      weakMagnitude: 0.65,
    });
    this.blockedUntil = at + duration;
    this.nextTapAt = this.blockedUntil;
    return played;
  }

  stop() {
    this.clearPattern();
    this.externalHaptics?.stop?.();
    for (const actuator of this.actuators()) {
      try {
        const result = actuator?.reset?.();
        if (result?.catch) result.catch(() => {});
      } catch { /* best-effort cleanup */ }
    }
  }
}

// HP 피격용 Xbox 출력은 화면 조작의 activeIndex와 분리한다. DualSense가 함께
// 연결돼 있어도 Xbox 자체를 골라, 원래 검증된 표준 Gamepad 진동 경로로 보낸다.
export class XboxDamageHaptics extends GamepadHitHaptics {
  pad() {
    // Xbox만 고른다. PlayStation이 함께 연결돼 있어도 그 액추에이터에는 닿지 않는다.
    const pads = Array.from(this.getGamepads?.() ?? [])
      .filter((pad) => pad?.connected !== false);
    const preferred = pads.find((pad) => Number(pad?.index) === this.activeIndex
      && gamepadFamily(pad?.id) === 'xbox');
    return preferred
      ?? pads.find((pad) => gamepadFamily(pad?.id) === 'xbox' && hasHapticActuator(pad))
      ?? pads.find((pad) => gamepadFamily(pad?.id) === 'xbox')
      ?? null;
  }

  play({ duration, strongMagnitude, weakMagnitude }) {
    const pad = this.pad();
    if (!pad) return false;

    // 타격 진동이 정상 동작하던 최초 Xbox 배선과 같은 전송 방식이다.
    // Chrome/XInput은 vibrationActuator.effects를 빈 배열로 내놓는 경우가 있어
    // 지원 목록으로 거르면 실제 dual-rumble 액추에이터도 호출되지 않는다.
    const actuator = pad.vibrationActuator ?? pad.hapticActuators?.[0] ?? null;
    if (!actuator) return false;
    try {
      if (typeof actuator.playEffect === 'function') {
        const result = actuator.playEffect('dual-rumble', {
          startDelay: 0, duration, strongMagnitude, weakMagnitude,
        });
        if (result?.catch) result.catch(() => {});
        return true;
      }
      if (typeof actuator.pulse === 'function') {
        const result = actuator.pulse(Math.max(strongMagnitude, weakMagnitude), duration);
        if (result?.catch) result.catch(() => {});
        return true;
      }
    } catch { /* Xbox 진동 실패가 전투 루프를 멈추면 안 된다. */ }
    return false;
  }
}

// 기존 호출부와 외부 프로브가 끊기지 않게 남기는 호환 이름.
/** Player HP loss is broadcast to every connected haptic controller. */
export class PlayerDamageHaptics extends GamepadHitHaptics {
  pads() {
    return Array.from(this.getGamepads?.() ?? []).filter((pad) => pad?.connected !== false);
  }

  playOnPad(pad, options) {
    const candidates = [];
    const actuators = [pad?.vibrationActuator, ...(pad?.hapticActuators ?? [])]
      .filter((actuator, index, all) => actuator && all.indexOf(actuator) === index);
    for (const actuator of actuators) {
      if (typeof actuator.playEffect === 'function') {
        // Chromium may expose an empty effects list while XInput dual-rumble
        // still works, so call the transport and fall back on rejection.
        candidates.push(() => actuator.playEffect('dual-rumble', {
          startDelay: 0, ...options,
        }));
        candidates.push(() => actuator.playEffect('trigger-rumble', {
          startDelay: 0, ...options, leftTrigger: 0, rightTrigger: 0,
        }));
      }
      if (typeof actuator.pulse === 'function') {
        candidates.push(() => actuator.pulse(
          Math.max(options.strongMagnitude, options.weakMagnitude), options.duration));
      }
    }
    return this.tryCandidates(candidates);
  }

  hurt(damage = 0) {
    if (Math.max(0, Number(damage) || 0) <= 0) return false;
    const at = this.now();
    if (at < this.blockedUntil) return false;
    this.clearPattern();
    const options = { duration: 150, strongMagnitude: 1, weakMagnitude: 0.65 };
    const directPlayStation = this.externalHaptics?.connected
      && this.externalHaptics.play(options);
    let played = !!directPlayStation;
    for (const pad of this.pads()) {
      if (directPlayStation && gamepadFamily(pad?.id) === 'playstation') continue;
      played = this.playOnPad(pad, options) || played;
    }
    this.blockedUntil = at + options.duration;
    this.nextTapAt = this.blockedUntil;
    return played;
  }

  stop() {
    this.clearPattern();
    this.externalHaptics?.stop?.();
    for (const pad of this.pads()) {
      const actuators = [pad?.vibrationActuator, ...(pad?.hapticActuators ?? [])];
      for (const actuator of actuators) {
        try {
          const result = actuator?.reset?.();
          if (result?.catch) result.catch(() => {});
        } catch { /* best-effort cleanup */ }
      }
    }
  }
}

export const XboxHitHaptics = GamepadHitHaptics;
